import {
  SLOT_DEFS,
  SLOT,
  INAMES,
  ALL_STAT_KEYS,
  STAT_KEY_MIGRATION,
  RARITY_BASE,
  ITEM_LEVEL_CAP,
} from "../constants/items";
import { RARITY_MULT } from "../constants/design";

const rid = (p: string) => p + Math.random().toString(36).slice(2, 10);

// ── Stats identitaires par slot (chaque item garantit au moins 1 de ces 2 stats) ──
export const IDENTITY_STATS: Record<string, [string, string]> = {
  head:       ["technique", "defense"],
  top:        ["defense",   "vitesse"],
  shoes:      ["technique", "vitesse"],
  ball:       ["physique",  "technique"],
  background: ["physique",  "vitesse"],
  familiar:   ["physique",  "defense"],
};

// Nombre de stats par rareté — Ultra = 3 (même qu'Epic, supérieur par budget)
const STAT_COUNT: Record<string, number> = {
  commun: 1,
  rare:   2,
  epic:   3,
  ultra:  3,
};

/** Multiplicateur de scaling selon le niveau du joueur au moment du drop.
 *  L1–L50 : 1 + L × 0.05  (×1.05 → ×3.5)
 *  L51–L100: 3.5 + (L-50) × 0.07  (×3.57 → ×7.0) */
export const levelScaling = (level: number): number => {
  const l = Math.min(Math.max(level, 1), ITEM_LEVEL_CAP);
  if (l <= 50) return 1 + l * 0.05;
  return 3.5 + (l - 50) * 0.07;
};

// ── Base vitality (level-scaled HP floor, before items) ───────────────────────
// Every player carries a guaranteed vitesse floor that grows with their level,
// so HP no longer depends purely on the item RNG. Starts at 20 at level 1 and
// gains VITALITY_PER_LEVEL per level (L1=20, L30=78, L50=118). Applied globally
// (both getEquipmentStats and cStats) so the profile/Duel/League power and the
// PVE HP all use the same floor.
export const BASE_VITALITY_L1 = 20;
export const VITALITY_PER_LEVEL = 2;
export const baseVitality = (level: number): number =>
  BASE_VITALITY_L1 + (Math.max(1, level) - 1) * VITALITY_PER_LEVEL;

// ── PVE energy affix (ball only) ──────────────────────────────────────────────
// From this level, epic & legendary (ultra) BALLS have a chance to also grant
// +1 energy per turn in Stadium Trial — a large action-economy bonus, kept rare
// and high-rarity on purpose.
export const BALL_ENERGY_AFFIX_MIN_LEVEL = 10;
export const BALL_ENERGY_AFFIX_CHANCE = 0.25;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Sélectionne les stats d'un item en garantissant au moins 1 stat identitaire du slot. */
function selectStats(slotKey: string, rarityKey: string): string[] {
  const identity = IDENTITY_STATS[slotKey] ?? [ALL_STAT_KEYS[0], ALL_STAT_KEYS[1]] as [string, string];
  const count = STAT_COUNT[rarityKey] ?? 1;

  if (count === 1) {
    // 1 stat = forcément une des 2 stats identitaires
    return [identity[Math.floor(Math.random() * 2)]];
  }

  if (count === 2) {
    // Garantir 1 stat identitaire, la 2e = n'importe laquelle des 3 restantes
    const first = identity[Math.floor(Math.random() * 2)];
    const pool = ALL_STAT_KEYS.filter(s => s !== first);
    const second = pool[Math.floor(Math.random() * pool.length)];
    return shuffle([first, second]);
  }

  // count === 3 : exclure exactement 1 stat des 4.
  // On ne peut exclure qu'1 des 2 stats identitaires max → l'autre reste toujours.
  const excludeIdx = Math.floor(Math.random() * ALL_STAT_KEYS.length);
  return shuffle(ALL_STAT_KEYS.filter((_, i) => i !== excludeIdx));
}

/** Distribue `budget` de façon aléatoire entre `count` stats (total stable, profil variable). */
function distributeRandom(budget: number, count: number): number[] {
  const weights = Array.from({ length: count }, () => Math.random());
  const wSum = weights.reduce((a, b) => a + b, 0);
  const rounded = weights.map(w => Math.max(1, Math.round((w / wSum) * budget)));
  // Correction du drift d'arrondi sur la valeur max
  const diff = Math.round(budget) - rounded.reduce((a, b) => a + b, 0);
  if (diff !== 0) {
    const maxIdx = rounded.reduce((best, v, i) => v > rounded[best] ? i : best, 0);
    rounded[maxIdx] = Math.max(1, rounded[maxIdx] + diff);
  }
  return rounded;
}

/** Remappe les anciennes clés de stats (items stockés en base) vers les 4 stats
 *  d'action actuelles. Sans effet sur un item déjà à jour. */
export function migrateStats(
  stats: Record<string, number>
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k in stats) {
    const key = STAT_KEY_MIGRATION[k] ?? k;
    out[key] = (out[key] ?? 0) + stats[k];
  }
  return out;
}

/* ─── Stats d'item dépendantes du niveau du joueur ─────────────────────────────
   Budget = Base(rareté) × levelScaling(niveau) × roll ±10 %
   Distribution aléatoire entre les stats sélectionnées.                          */

export function generateItemStats(
  slotKey: string,
  rarityKey: string,
  level = 1
): Record<string, number> {
  const base = RARITY_BASE[rarityKey] ?? RARITY_BASE.commun;
  const roll = 0.9 + Math.random() * 0.2; // ±10 %
  const budget = base * levelScaling(level) * roll;

  const stats = selectStats(slotKey, rarityKey);
  const values = distributeRandom(budget, stats.length);

  const out: Record<string, number> = {};
  stats.forEach((s, i) => { out[s] = values[i]; });

  // PVE energy affix: rare high-rarity ball bonus (+1 energy/turn in Stadium Trial).
  // Consumed by the interactive combat engine; ignored by the 4-stat power sum.
  if (
    slotKey === "ball" &&
    (rarityKey === "epic" || rarityKey === "ultra") &&
    level >= BALL_ENERGY_AFFIX_MIN_LEVEL &&
    Math.random() < BALL_ENERGY_AFFIX_CHANCE
  ) {
    out.energy = 1;
  }
  return out;
}

/** Vérifie la génération en console (à appeler depuis la dev tools). */
export function debugItemGen(): void {
  const rarities = ["commun", "rare", "epic", "ultra"] as const;
  const slots = ["head", "top", "shoes", "ball", "background", "familiar"];

  console.group("=== Item Generation Debug ===");

  console.log(
    `Scaling: L1=${levelScaling(1).toFixed(2)}  L50=${levelScaling(50).toFixed(2)}  L100=${levelScaling(100).toFixed(2)}`
  );

  // Stat count par rareté
  for (const rar of rarities) {
    const it = mkItem(rar, 50);
    if (!it) continue;
    const n = Object.keys(it.stats || {}).length;
    console.log(`[L50 ${rar}] ${it.slot} "${it.name}" → ${n} stats`, it.stats);
  }

  // Scaling Epic L50 vs L100
  const e50 = generateItemStats("ball", "epic", 50);
  const e100 = generateItemStats("ball", "epic", 100);
  console.log(`Epic ball L50 total=${Object.values(e50).reduce((a,b)=>a+b,0)}`, e50);
  console.log(`Epic ball L100 total=${Object.values(e100).reduce((a,b)=>a+b,0)}`, e100);

  // Contrainte stats identitaires (100 rolls par slot)
  for (const slot of slots) {
    const id = IDENTITY_STATS[slot];
    let violations = 0;
    for (let i = 0; i < 100; i++) {
      const s = generateItemStats(slot, "commun", 1);
      if (!id.some(k => k in s)) violations++;
    }
    if (violations > 0) console.error(`VIOLATION ${slot}: manque stat identitaire ${violations}/100`);
    else console.log(`✓ ${slot} [${id.join("/")}]: identité OK sur 100 rolls`);
  }

  console.groupEnd();
}

export function iStats(slot: string, rar: string): Record<string, number> {
  const def = SLOT[slot] || { stats: [] };
  const mult = RARITY_MULT[rar] || 1;
  const o: Record<string, number> = {};
  def.stats.forEach((st: string, i: number) => {
    o[st] = Math.round((5 - i) * mult);
  });
  return o;
}

export const getStats = (it: { stats?: Record<string, number>; slot: string; rarity: string }) =>
  it.stats && Object.keys(it.stats).length > 0
    ? migrateStats(it.stats)
    : iStats(it.slot, it.rarity);

export const iPower = (it: { stats?: Record<string, number>; slot: string; rarity: string }) =>
  Object.values(getStats(it)).reduce((a, b) => a + b, 0);

export const iName = (it: { name?: string; slot: string; rarity: string }) => {
  if (it.name) return it.name;
  const arr = INAMES[it.slot]?.[it.rarity];
  return Array.isArray(arr)
    ? arr[Math.floor(Math.random() * arr.length)]
    : arr || it.slot;
};

/** Returns the display-skin name for image lookups — cosmetic override when set, real name otherwise.
 *  Never use this for gameplay logic (familiar ownership, stat lookups, etc.). */
export const iSkin = (it: { name: string; cosmetic?: string }) =>
  it.cosmetic ?? it.name;

export const mkItem = (rar: string, level = 1) => {
  const rollableSlots = SLOT_DEFS.filter((s) => (s as any).rollable !== false);

  // Only pick from slots that have at least 1 item defined for this rarity
  const validSlots = rollableSlots.filter(
    (s) => (INAMES[s.key]?.[rar]?.length ?? 0) > 0,
  );

  if (validSlots.length === 0) {
    console.error(`[mkItem] No slot has items for rarity="${rar}" — check INAMES`);
    return null;
  }

  const slotDef = validSlots[Math.floor(Math.random() * validSlots.length)];
  const slot = slotDef.key;
  const arr = INAMES[slot][rar]!;
  const name = arr[Math.floor(Math.random() * arr.length)];

  if (!name) {
    console.error(`[mkItem] Undefined name for slot="${slot}" rarity="${rar}"`);
    return null;
  }

  const stats = generateItemStats(slot, rar, level);
  return { iid: rid("i_"), slot, rarity: rar as "commun" | "rare" | "epic" | "ultra", name, stats };
};

export function rollRar(odds: [string, number][]): string {
  let r = Math.random() * 100,
    acc = 0;
  for (const [rar, w] of odds) {
    acc += w;
    if (r < acc) return rar;
  }
  return "commun";
}
